import React, { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Form } from "react-bootstrap";
import "./TrendPane.css";

const hebrewMonths = [
  "ינואר",
  "פברואר",
  "מרץ",
  "אפריל",
  "מאי",
  "יוני",
  "יולי",
  "אוגוסט",
  "ספטמבר",
  "אוקטובר",
  "נובמבר",
  "דצמבר",
];

const TrendChart = React.memo(({ data, isModal = false }) => {
  const height = isModal ? 450 : 175;

  if (!data || data.length < 2) {
    const dummyData = [
      { name: "ינואר", ספורטאים: 10 },
      { name: "פברואר", ספורטאים: 20 },
    ];

    return (
      <div className="trend-chart-container">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={dummyData} height={height}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
            <XAxis
              dataKey="name"
              stroke="#636e72"
              tick={{ fontSize: 14 }}
              interval={0}
              textAnchor="middle"
              height={60}
              padding={{ left: 30, right: 30 }}
            />
            <YAxis tickMargin={20} stroke="#636e72" domain={[0, 35]} />
            <Line
              type="monotone"
              dataKey="ספורטאים"
              stroke="#77dd77"
              strokeWidth={3}
              dot={{ fill: "#77dd77", r: 5 }}
              activeDot={{ r: 7 }}
              opacity={0.4}
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="no-data-overlay">
          <div className="no-data-message">אין מספיק נתונים עדיין</div>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} height={height}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
        <XAxis
          dataKey="name"
          stroke="#636e72"
          tick={{ fontSize: 14 }}
          interval={0}
          textAnchor="middle"
          height={60}
          padding={{ left: 30, right: 30 }}
        />
        <YAxis tickMargin={20} stroke="#636e72" />
        <Tooltip
          contentStyle={{
            backgroundColor: "#fff",
            border: "none",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        />
        <Line
          type="monotone"
          dataKey="ספורטאים"
          stroke="#77dd77"
          strokeWidth={2}
          dot={{ fill: "#77dd77", r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
});

const GroupSelector = React.memo(({ selectedGroup, groups, onGroupChange }) => (
  <div className="group-selector">
    <Form.Select
      value={selectedGroup}
      onChange={onGroupChange}
      style={{
        maxWidth: "300px",
        margin: "0 auto",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        backgroundColor: "#fff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        cursor: "pointer",
        transition: "all 0.2s ease",
        textAlign: "right",
        direction: "rtl",
        appearance: "none",
        backgroundImage:
          "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "left 0.75rem center",
        backgroundSize: "16px 12px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <option value="all">כל הקבוצות</option>
      {groups.map((group) => (
        <option key={group.id} value={group.id}>
          {group.name}, סניף {group.dojo}
        </option>
      ))}
    </Form.Select>
  </div>
));

const TrendPane = ({ statsReports, groups, isModal = false }) => {
  const [selectedGroup, setSelectedGroup] = useState("all");

  const data = useMemo(() => {
    if (!statsReports || statsReports.length < 2) return null;

    const recentReports = statsReports
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 12)
      .reverse();

    return recentReports.map((report) => {
      const date = new Date(report.timestamp.seconds * 1000);
      const month = hebrewMonths[date.getMonth()];
      const value =
        selectedGroup === "all"
          ? report.totalStudentsInGroups
          : report.studentsPerGroup?.[selectedGroup] || 0;

      return {
        name: month,
        ספורטאים: value || 0,
      };
    });
  }, [statsReports, selectedGroup]);

  const handleGroupChange = (e) => {
    setSelectedGroup(e.target.value);
  };

  return (
    <div className="trend-pane">
      <GroupSelector
        selectedGroup={selectedGroup}
        groups={groups}
        onGroupChange={handleGroupChange}
        isModal={isModal}
      />
      <TrendChart data={data} isModal={isModal} />
    </div>
  );
};

export default React.memo(TrendPane);
